/**
 * CallLeadPopup — modal overlay shown while a direct-call (popup) lifecycle
 * is active. Rendered by CallLeadPopupProvider whenever `currentLead !== null`.
 *
 * Auto-dials on mount once the Twilio Device is `ready` (which is usually
 * already the case because TwilioProvider hoisted to app root pre-warms the
 * Device when the agent logs in) — EXCEPT in `inbound` mode, where the popup
 * surfaces an already-accepted incoming call and dials nothing. The popup
 * stays open through the entire call lifecycle and renders different bottom
 * controls per state:
 *
 *   connecting / ringing       → Mute + End Call (header: Minimize)
 *   in-call                    → Mute + End Call (header: Minimize) + live transcript
 *   ended / error              → Close (no Mute, no End Call; header: X)
 *
 * While the call is ACTIVE the header offers Minimize instead of X: the modal
 * hides (this component stays mounted rendering null) and the floating
 * ActiveCallIndicator takes over so the agent can navigate the app mid-call;
 * its Expand button brings the popup back. If the call reaches a terminal
 * state while minimized, the provider auto-expands so the outcome is seen.
 *
 * Closing the popup is the user's explicit signal to release the call slot
 * — the next CallLeadButton click anywhere in the app then works again. The
 * twilio context's resetCallState() flips the state back to "ready" so the
 * Device is immediately available for a new call.
 */
import { useEffect, useRef, useState } from "react";
import { useTwilioContext } from "@/context/twilio.context";
import CallPopupHeader from "./CallPopupHeader";
import CallIdentity from "./CallIdentity";
import CallControls from "./CallControls";
import CallTranscriptPanel from "./CallTranscriptPanel";
import { STATE_PILLS } from "./constants";
import type { Props } from "./types";

const CallLeadPopup = ({ lead, onClose, minimized, onMinimize, inbound }: Props) => {
  const {
    state,
    isCallActive,
    isMuted,
    isOnHold,
    callStartedAt,
    errorMessage,
    isNetworkError,
    isReconnecting,
    callEndReason,
    connect,
    mute,
    unmute,
    hold,
    unhold,
    hangup,
    resetCallState,
  } = useTwilioContext();

  // Tick once a second while in-call so the displayed duration updates.
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    if (state !== "in-call") return;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [state]);

  // Auto-dial once per popup mount. connect() runs the full lazy sequence
  // (mint lead-scoped token → build + register Device → dial), so there's no
  // pre-warmed "ready" state to wait for. `leadId` mints the token. This is a
  // WS-silent surface: NO `agent:engaging` / `agent:disengaged` is emitted for
  // popup calls (those only flow from the widget click + dedicated screen). No
  // `originPath` either — the popup is its own persistent indicator (modal,
  // follows the agent across navigation), so the floating ActiveCallIndicator
  // stays hidden while the popup owns the call.
  const autoDialedRef = useRef(false);
  useEffect(() => {
    // Inbound mode: the call was ACCEPTED before this popup opened — it is
    // already live on the twilio context, so there is nothing to dial. (The
    // context's in-flight dedup would swallow the connect() anyway; this
    // makes the contract explicit instead of relying on that.)
    if (inbound) return;
    if (autoDialedRef.current) return;
    if (!lead.phoneNumber) return;
    autoDialedRef.current = true;
    connect({
      phoneNumber: lead.phoneNumber,
      leadId: lead.id,
      leadName: lead.name,
    });
  }, [connect, inbound, lead.phoneNumber, lead.id, lead.name]);

  const isCallOver = state === "ended" || state === "error";

  const handleClose = () => {
    // Belt-and-suspenders: if the agent closes while still connected (e.g.
    // they hit the X before End Call), hang up first so the call doesn't
    // leak. `isCallActive` comes from the context and includes
    // "initializing" — closing during token mint requests an abort so the
    // pending dial never rings the lead.
    if (isCallActive) {
      try { hangup(); } catch { /* noop */ }
    }
    resetCallState();
    onClose();
  };

  const handleEndCall = () => {
    hangup();
    // Don't close immediately — let the Twilio events transition state to
    // "ended" so the user sees the "CALL ENDED" pill briefly. The Close
    // button (or the X) is then the explicit dismiss.
  };

  const pill =
    state === "error" && isNetworkError
      ? STATE_PILLS["no-internet"]
      : state === "in-call" && isOnHold
      ? STATE_PILLS["on-hold"]
      : (STATE_PILLS[state] ?? STATE_PILLS.idle);

  // Minimized: render nothing, but STAY MOUNTED — the auto-dial guard and the
  // in-call timer live in this instance, and the floating ActiveCallIndicator
  // is representing the call on screen. All hooks above still run.
  if (minimized) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Active call"
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
    >
      <div className="relative w-full max-w-md rounded-2xl border border-[#4A515A] bg-gradient-to-b from-[#1B1F28] to-[#0D1018] shadow-[0_20px_60px_rgba(0,0,0,0.6)] overflow-hidden">
        {/* Soft inner glow at the top so the modal feels lifted off the dim
            backdrop without using an aggressive border. */}
        <div className="pointer-events-none absolute inset-x-0 top-0 h-32 bg-gradient-to-b from-blue-500/5 to-transparent" />

        <CallPopupHeader
          pill={pill}
          onClose={handleClose}
          onMinimize={onMinimize}
          showMinimize={isCallActive}
        />

        <CallIdentity
          lead={lead}
          state={state}
          isOnHold={isOnHold}
          callStartedAt={callStartedAt}
          now={now}
          callEndReason={callEndReason}
          errorMessage={errorMessage}
          isNetworkError={isNetworkError}
          isReconnecting={isReconnecting}
        />

        {state === "in-call" && <CallTranscriptPanel lead={lead} />}

        <CallControls
          isCallOver={isCallOver}
          onClose={handleClose}
          isMuted={isMuted}
          mute={mute}
          unmute={unmute}
          isOnHold={isOnHold}
          hold={hold}
          unhold={unhold}
          state={state}
          onEndCall={handleEndCall}
        />
      </div>
    </div>
  );
};

export default CallLeadPopup;
